import { fork } from '@cortex/utils/fork';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { useAuth } from '../../auth/AuthProvider';
import { useTheopolis, type IThread } from '../../lib/theopolis';

const formantChatAgentId = 'agt_formant_chat';

/**
 * Sidebar rail listing this user's Formant-chat threads.
 *
 * Scope note: the server (`GET /threads`) already filters by the caller's
 * `orgId` + `userId` (DEV-16718), so we never see anyone else's threads.
 * We additionally filter client-side to the `agt_formant_chat` agent —
 * threads bound to the retired `agt_chat` (legacy Dr. Theopolis) or `agt_theopolis`
 * (refinement) are surfaced by their own routes, not by this rail.
 *
 * Client-side agent filter is intentional. The alternative would be a
 * server-side `agentId=` query param, but the fetch is small enough
 * (a few dozen rows per user in typical use) that we prefer to keep the
 * list endpoint stable and do the split here.
 */
export function ThreadList(props: {
  activeThreadId: string | null;
  onSelectThread: (thread: IThread) => void;
  onNewChat: () => void;
  newChatBusy: boolean;
}) {
  const { activeThreadId, onSelectThread, onNewChat, newChatBusy } = props;
  const theo = useTheopolis();
  const qc = useQueryClient();
  const { ready: authReady, token } = useAuth();

  // Local set of optimistically-archived thread ids. Threads in this set are
  // hidden immediately on archive click while the PATCH is in flight; on error
  // we clear the id and refetch to restore the list.
  const [optimisticArchived, setOptimisticArchived] = useState<ReadonlySet<string>>(new Set());

  // Inline rename state.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');

  const threadsQuery = useQuery({
    queryKey: ['theopolis', 'threads'],
    queryFn: () => theo.get('threads').json<IThread[]>(),
    enabled: authReady && Boolean(token),
  });

  const renameThread = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      theo.patch(`threads/${id}`, { json: { title } }).json<IThread>(),
    onSuccess: () => {
      fork(qc.invalidateQueries({ queryKey: ['theopolis', 'threads'] }));
    },
  });

  const archiveThread = useMutation({
    mutationFn: (threadId: string) =>
      theo.patch(`threads/${threadId}`, { json: { archived: true } }).json<IThread>(),
    onMutate: (threadId) => {
      // Optimistically hide the row immediately.
      setOptimisticArchived((prev) => {
        const next = new Set(prev);
        next.add(threadId);
        return next;
      });
    },
    onSuccess: (_data, _threadId) => {
      fork(qc.invalidateQueries({ queryKey: ['theopolis', 'threads'] }));
    },
    onError: (_err, threadId) => {
      // Restore the row and let a refetch reconcile the true server state.
      setOptimisticArchived((prev) => {
        const next = new Set(prev);
        next.delete(threadId);
        return next;
      });
      fork(qc.invalidateQueries({ queryKey: ['theopolis', 'threads'] }));
    },
  });

  const rows = useMemo(() => {
    const all = threadsQuery.data ?? [];
    // Formant-chat threads only. Archived ones are hidden; V0 has no
    // "show archived" toggle (deferred to V1).
    return all
      .filter(
        (t) => t.agentId === formantChatAgentId && !t.archived && !optimisticArchived.has(t.id),
      )
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }, [threadsQuery.data, optimisticArchived]);

  return (
    <div className="thread-list" role="navigation" aria-label="Chat history">
      <button
        type="button"
        className="btn btn--secondary btn--sm thread-list__new"
        onClick={onNewChat}
        disabled={newChatBusy}
        title="Start a new chat"
      >
        <PlusIconInline />
        <span>New chat</span>
      </button>

      {threadsQuery.isLoading ? (
        <div className="thread-list__state">Loading…</div>
      ) : threadsQuery.error ? (
        <div className="thread-list__state thread-list__state--error">
          Failed to load: {(threadsQuery.error as Error).message}
        </div>
      ) : rows.length === 0 ? (
        <div className="thread-list__state">No prior chats.</div>
      ) : (
        <ul className="thread-list__rows">
          {rows.map((t) => (
            <li key={t.id} className="thread-list__item">
              <button
                type="button"
                className={
                  t.id === activeThreadId
                    ? 'thread-list__row thread-list__row--active'
                    : 'thread-list__row'
                }
                onClick={() => onSelectThread(t)}
                title={t.title ?? 'Untitled chat'}
              >
                {editingId === t.id ? (
                  <input
                    className="thread-list__title-input"
                    value={editingValue}
                    autoFocus={true}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setEditingValue(e.target.value)}
                    onBlur={() => {
                      if (editingValue.trim()) {
                        renameThread.mutate({ id: t.id, title: editingValue.trim() });
                      }
                      setEditingId(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (editingValue.trim()) {
                          renameThread.mutate({ id: t.id, title: editingValue.trim() });
                        }
                        setEditingId(null);
                      } else if (e.key === 'Escape') {
                        setEditingId(null);
                      }
                    }}
                  />
                ) : (
                  <span
                    className="thread-list__title"
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      setEditingId(t.id);
                      setEditingValue(t.title ?? '');
                    }}
                  >
                    {t.title ?? 'Untitled chat'}
                  </span>
                )}
                <span className="thread-list__meta">{formatRelative(t.updatedAt)}</span>
              </button>
              <button
                type="button"
                className="thread-list__archive"
                title="Archive chat"
                aria-label="Archive chat"
                onClick={(e) => {
                  e.stopPropagation();
                  archiveThread.mutate(t.id);
                }}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PlusIconInline() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.max(0, Math.floor((now - then) / 1000));
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 2) return 'yesterday';
  if (diffDay < 7) return new Date(iso).toLocaleDateString(undefined, { weekday: 'short' });
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export { formantChatAgentId, formatRelative };
